import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { Drawer, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import I18n from '../../../../components/shared/i18n/I18n.component';
import Typography from '../../../../components/shared/Typography/Typography.component';
import ButtonV2 from '../../../../components/shared/Buttons/ButtonV2.container';
import ExtraInfo from '../../../../components/shared/ExtraInfo/ExtraInfo.component';
import colors from '../../../../config/colors';
import { i18nService } from '../../../../services/i18n.service';
import { snackbarService } from '../../../../services/snackbar.service';
import { UserModel } from '../../../../models/users.model';
import { isRowSelectable } from '../UsersTablePage.constants';
import {
	checkCanSendInvitation,
	sendUserInvitations,
	isConnectionError,
} from './InviteUsersDrawer.api';

type InviteUsersDrawerProps = {
	open: boolean;
	onClose: () => void;
	onSent: () => void;
	selectAll: boolean;
	selectedItems: number[];
	initialCount: number;
	localUsers: UserModel[];
	unitId: number;
};

/** the loaded row, with the eligibility the server answered for it */
type InviteCandidate = UserModel & { canSend: boolean };

const useStyles = makeStyles({
	paper: {
		width: 400,
		maxWidth: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		padding: 24,
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
});

const toast = (key: string, success: boolean) =>
	snackbarService.show({ message: i18nService.translate(key), success });

/**
 * Confirmation panel for the users table 'invite' selection mode. Lists the
 * selected users and marks the ones the server says cannot be invited - those
 * are shown but left out of the request.
 */
const InviteUsersDrawer = ({
	open,
	onClose,
	onSent,
	selectAll,
	selectedItems,
	initialCount,
	localUsers,
	unitId,
}: InviteUsersDrawerProps) => {
	const classes = useStyles();

	const [candidates, setCandidates] = useState<InviteCandidate[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);

	/** ignore an eligibility response that arrives after the drawer was closed/reopened */
	const requestId = useRef(0);

	useEffect(() => {
		if (!open) {
			return;
		}

		const currentRequest = ++requestId.current;
		setCandidates(null);
		setSending(false);
		setLoading(true);

		// the eligibility api answers about users it is given - the names and ids
		// come from the loaded rows, 'select all' meaning every invitable one of them
		const selectedUsers = (localUsers || []).filter(user =>
			selectAll ? isRowSelectable(user, 'invite') : selectedItems.includes(user.profileId),
		);

		checkCanSendInvitation(selectedUsers.map(user => user.teudatZeut), unitId)
			.then(res => {
				if (currentRequest !== requestId.current) {
					return;
				}
				const canSendByUserId = new Map(
					(res || []).map(result => [result.userId, result.canSend]),
				);
				// a user the server did not answer about is treated as invitable
				setCandidates(
					selectedUsers.map(user => ({
						...user,
						canSend: canSendByUserId.get(user.teudatZeut) ?? true,
					})),
				);
				setLoading(false);
			})
			.catch(() => {
				if (currentRequest !== requestId.current) {
					return;
				}
				// the drawer stays usable without the server flags - every selected
				// user is treated as invitable
				setCandidates(
					selectedUsers.map(user => ({ ...user, canSend: true })),
				);
				setLoading(false);
			});
	}, [open]);

	const eligibleUserIds = useMemo(
		() =>
			(candidates || [])
				.filter(candidate => candidate.canSend)
				.map(candidate => candidate.teudatZeut),
		[candidates],
	);

	const onConfirm = async () => {
		setSending(true);

		try {
			// the flagged users stay in the list but are never sent
			await sendUserInvitations(eligibleUserIds, unitId);
			toast('usersManagement.inviteDrawer.successToast', true);
			onSent();
		} catch (err) {
			if (isConnectionError(err)) {
				toast('usersManagement.inviteDrawer.errorToast', false);
				setSending(false);
				return;
			}
			// the server answered - the invitations are considered sent whatever it says
			toast('usersManagement.inviteDrawer.successToast', true);
			onSent();
		}
	};

	return (
		<Drawer
			anchor='left'
			open={open}
			onClose={onClose}
			classes={{ paper: classes.paper }}
		>
			<View style={styles.header}>
				<I18n
					size={20}
					color='primaryDarkBlue'
					font='bold'
					count={candidates ? candidates.length : initialCount}
				>
					usersManagement.inviteDrawer.title
				</I18n>
				<TouchableOpacity
					onPress={onClose}
					hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
				>
					<CloseIcon style={{ fontSize: 20, color: colors.greyText }} />
				</TouchableOpacity>
			</View>

			<I18n size={17} color='primaryDarkBlue' style={styles.question}>
				usersManagement.inviteDrawer.question
			</I18n>

			<ScrollView style={styles.list}>
				<View style={styles.listContent}>
					{loading ? (
						<ActivityIndicator color={colors.primary} />
					) : (
						(candidates || []).map(candidate => (
							<View key={candidate.profileId} style={styles.row}>
								<Typography size={15} color='clockRecordBlue'>
									{`•`}
								</Typography>
								<Typography size={15} color='clockRecordBlue'>
									{`${candidate.fullName}`}
								</Typography>
								{!candidate.canSend && (
									<ExtraInfo
										infoText='usersManagement.inviteDrawer.cannotInviteTooltip'
										iconName='alertCircle'
										mode='big'
										variant='card'
										placement='top'
									/>
								)}
							</View>
						))
					)}
				</View>
			</ScrollView>

			<View style={styles.footer}>
				<ButtonV2
					preset='primary'
					onPress={onConfirm}
					disabled={loading || sending || eligibleUserIds.length === 0}
				>
					<I18n color='white' size={14} numberOfLines={1}>
						usersManagement.inviteDrawer.confirm
					</I18n>
				</ButtonV2>
				<ButtonV2 preset='outline' onPress={onClose}>
					<I18n color='titlePrimary' size={14} numberOfLines={1}>
						general.cancel
					</I18n>
				</ButtonV2>
			</View>

			{sending && (
				<View style={styles.sendingOverlay}>
					<ActivityIndicator color={colors.primary} size='large' />
				</View>
			)}
		</Drawer>
	);
};

const styles = StyleSheet.create({
	header: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexShrink: 0,
	},
	question: {
		marginTop: 24,
		flexShrink: 0,
	},
	// writingDirection 'rtl' -> 'ltr' is for the scrollbar to stick in left
	list: {
		flex: 1,
		minHeight: 0,
		marginTop: 24,
		writingDirection: 'rtl',
	},
	listContent: {
		writingDirection: 'ltr',
	},
	row: {
		flexDirection: 'row-reverse',
		alignItems: 'center',
		marginBottom: 12,
		gap: 8
	},
	footer: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
		height: 40,
		flexShrink: 0,
		marginTop: 16,
	},
	sendingOverlay: {
		position: 'absolute',
		left: 0,
		right: 0,
		top: 0,
		bottom: 0,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.backgroundOpacity,
	},
});

export default InviteUsersDrawer;
